import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import * as React from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import type { PropertyCancellationLabelShape } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane'
import Hr from 'components/Common/Hr'
import ModalContext from 'contexts/ModalContext'
import RestrictedCancellationPolicyTimeline from 'components/Common/CancellationPolicies/RestrictedCancellationPolicyTimeline'
import type { RestrictedCancellationPolicyView } from 'hooks/useRestrictedCancellationView'

export interface PropertyCancellationPolicyViewProps {
  refundableStatus: Refundable
  refundDescription: string
  labelShape: PropertyCancellationLabelShape
  policyDescriptions: Array<string | undefined>
  restrictedView?: RestrictedCancellationPolicyView
  activationRef?: (node: Element | null) => void
}

const RENDER_INFO_ICON = () => <LineInfoCircleIcon size="XS" />

function PropertyCancellationPolicyView({
  refundableStatus,
  refundDescription,
  labelShape,
  policyDescriptions,
  restrictedView,
  activationRef,
}: PropertyCancellationPolicyViewProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const restrictedModalTitle = intl.formatMessage({
    id: 'V32JhNbR3r9Xvqnu40eu',
    defaultMessage: 'Cancellation policy',
  })

  const openModal = useCallback(() => {
    showModal(<Modal title={restrictedView ? restrictedModalTitle : refundDescription}>
      {restrictedView && <RestrictedCancellationPolicyTimeline view={restrictedView} />}
      {restrictedView && <Hr size="md" />}
      {(restrictedView ? restrictedView.penaltyLines : policyDescriptions)
        .map((line, i) => <BodyText variant={restrictedView ? 'medium' : 'large'} as="p" key={i}>{ line }</BodyText>)}
      {!restrictedView && <HelpPane />}
    </Modal>)
  }, [showModal, refundDescription, restrictedModalTitle, policyDescriptions, restrictedView])

  const shieldIcon = refundableStatus === Refundable.Non
    ? <LineShieldSlashIcon />
    : <SolidShieldCheckIcon colour="highlight-primary-dark" />

  const cancellationLabel = useMemo<React.ReactNode>(() => {
    if (labelShape.kind === 'non-refundable') return refundDescription
    const refundLabel = restrictedView
      ? intl.formatMessage({
          id: '0BLh-rUoC7gz5it_nG00',
          defaultMessage: 'Flexible cancellation',
        })
      : refundDescription
    const styledRefund = <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span" wrap="no-wrap" key="refund">{refundLabel}</BodyText>
    if (labelShape.kind === 'date' && labelShape.hotelOnly) {
      return intl.formatMessage(
        {
          id: 'nsXp9-TaxkcfoC_Mgmn8',
          defaultMessage: '{refundType} until {date} (hotel only)',
        },
        { refundType: styledRefund, date: labelShape.formattedDate },
      )
    }
    if (labelShape.kind === 'date') {
      return intl.formatMessage(
        {
          id: 'nGO52oYKVABrD8dlQrKg',
          defaultMessage: '{refundType} until {date}',
        },
        { refundType: styledRefund, date: labelShape.formattedDate },
      )
    }
    if (labelShape.hotelOnly) {
      return intl.formatMessage({
        id: 'TU9nLufGKPNHd7PoClqw',
        defaultMessage: '{refundType} (hotel only)',
      }, { refundType: styledRefund })
    }
    return styledRefund
  }, [labelShape, intl, refundDescription, restrictedView])

  return <Clickable onClick={openModal} ref={activationRef}>
    <BodyTextBlock variant="medium" startIcon={shieldIcon}>
      <FormattedMessage
        id="aBR0Mwy9eVd8FXMnzQYq"
        defaultMessage="{cancellationLabel} <icon></icon>"
        
        values={{
          cancellationLabel,
          icon: RENDER_INFO_ICON,
        }}
      />
    </BodyTextBlock>
  </Clickable>
}

export default PropertyCancellationPolicyView
